import { useEffect, useLayoutEffect, useRef } from 'react';

import { shallowEqual, useSelector } from 'react-redux';

import { usePreferences } from '@/hooks/usePreferences';
import { selectUser } from '@/redux/user/selectors';
import { User } from '@/shared/types/user';

import { identifyUser, isInternalEmail } from './segment';

/**
 * Identifies the signed-in user, then enriches the identity once preferences
 * arrive. Call it once, from the authenticated layout.
 *
 * Two passes, because a page view is never re-emitted: the first runs in a
 * layout effect so it beats the `pageView` of the page underneath, and waiting
 * for preferences there would leave the first view of a session anonymous.
 */
export const useSegmentIdentity = () => {
  const user = useSelector(selectUser, shallowEqual) as User | null;
  const { userPreferences } = usePreferences();

  useLayoutEffect(() => {
    if (!user?.email) {
      return;
    }
    // Do not track moveo users in logrocket
    identifyUser(user, undefined, !isInternalEmail(user.email));
  }, [user]);

  // `usePreferences` mints a fresh object on every save, so guard on the user:
  // only the undefined -> defined transition is worth a second call.
  const enrichedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.email || !userPreferences || enrichedFor.current === user.user_id) {
      return;
    }
    enrichedFor.current = user.user_id;
    identifyUser(user, userPreferences);
  }, [user, userPreferences]);
};
